import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  Paper, 
  Grid,
  Container,
  TextField,
  Button,
  Divider,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  Schedule,
  Send,
  WhatsApp
} from '@mui/icons-material';
import api from '../../config/axiosConfig';

const EMPTY_FORM = {
  nombre: '',
  email: '',
  empresa: '',
  telefono: '',
  asunto: '',
  mensaje: ''
};

const WHATSAPP_URL = 'https://wa.me/522291294389';

const Contacto = () => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setResult(null);
    try {
      await api.post('/contact', formData);
      setFormData(EMPTY_FORM);
      setResult({ severity: 'success', text: 'Mensaje enviado. Te responderemos a la brevedad.' });
    } catch (err) {
      const text = err.response?.data?.error || 'No se pudo enviar el mensaje. Intenta de nuevo más tarde.';
      setResult({ severity: 'error', text });
    } finally {
      setSending(false);
    }
  };

  const contactInfo = [
    {
      icon: <LocationOn />,
      title: 'Dirección',
      content: 'Veracruz: Boca del Río, Veracruz\nCDMX: Ciudad de México',
      color: 'primary'
    },
    {
      icon: <Phone />,
      title: 'Teléfonos',
      content: 'Veracruz: 229 129 4389\nCDMX: 561 337 4651',
      color: 'success'
    },
    {
      icon: <Email />,
      title: 'Correos Electrónicos',
      content: 'Veracruz: info@conem.com.mx\nCDMX: rhernandez@conem.com.mx',
      color: 'info'
    },
    {
      icon: <Schedule />,
      title: 'Horarios de Atención',
      content: 'Lunes a Viernes: 9:00 - 18:00',
      color: 'warning'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box textAlign="center" mb={6}>
        <Typography variant="h3" color="primary" fontWeight="bold" gutterBottom>
          Contacto
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
          Estamos aquí para ayudarte. Contáctanos y descubre cómo podemos impulsar tu proyecto
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Información de contacto */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={6} sx={{ p: 4, borderRadius: 4, height: 'fit-content' }}>
            <Typography variant="h4" color="primary" fontWeight="bold" gutterBottom>
              Información de Contacto
            </Typography>
            <Divider sx={{ mb: 3, bgcolor: 'primary.main', height: 2 }} />
            
            <Grid container spacing={3}>
              {contactInfo.map((info, index) => (
                <Grid size={{ xs: 12 }} key={index}>
                  <Card 
                    elevation={2} 
                    sx={{ 
                      p: 3, 
                      borderRadius: 3,
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    <Box display="flex" alignItems="flex-start">
                      <Box 
                        sx={{ 
                          p: 2, 
                          borderRadius: 2, 
                          bgcolor: `${info.color}.main`, 
                          color: 'white',
                          mr: 3
                        }}
                      >
                        {info.icon}
                      </Box>
                      <Box>
                        <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                          {info.title}
                        </Typography>
                        <Typography 
                          variant="body1" 
                          color="text.secondary" 
                          sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}
                        >
                          {info.content}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Redes sociales */}
            <Box mt={4}>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Escríbenos por WhatsApp
              </Typography>
              <Box display="flex" gap={2} mt={2}>
                <IconButton
                  component="a"
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp"
                  sx={{
                    bgcolor: '#25d366', 
                    color: 'white',
                    '&:hover': { bgcolor: '#128c7e' }
                  }}
                >
                  <WhatsApp />
                </IconButton>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Formulario de contacto */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper 
            elevation={6} 
            sx={{ 
              p: 4, 
              borderRadius: 4,
              background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
            }}
          >
            <Typography variant="h4" color="primary" fontWeight="bold" gutterBottom>
              Envíanos un Mensaje
            </Typography>
            <Divider sx={{ mb: 3, bgcolor: 'primary.main', height: 2 }} />
            
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Nombre Completo"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    sx={{ bgcolor: 'white', borderRadius: 1 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Correo Electrónico"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    sx={{ bgcolor: 'white', borderRadius: 1 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Empresa"
                    name="empresa"
                    value={formData.empresa}
                    onChange={handleInputChange}
                    variant="outlined"
                    sx={{ bgcolor: 'white', borderRadius: 1 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleInputChange}
                    variant="outlined"
                    sx={{ bgcolor: 'white', borderRadius: 1 }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Asunto"
                    name="asunto"
                    value={formData.asunto}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    sx={{ bgcolor: 'white', borderRadius: 1 }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Mensaje"
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleInputChange}
                    required
                    multiline
                    rows={4}
                    variant="outlined"
                    sx={{ bgcolor: 'white', borderRadius: 1 }}
                  />
                </Grid>
                {result && (
                  <Grid size={{ xs: 12 }}>
                    <Alert severity={result.severity} onClose={() => setResult(null)}>{result.text}</Alert>
                  </Grid>
                )}
                <Grid size={{ xs: 12 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={sending}
                    startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <Send />}
                    sx={{ 
                      py: 1.5, 
                      px: 4, 
                      borderRadius: 3,
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    {sending ? 'Enviando...' : 'Enviar Mensaje'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Mapa o información adicional */}
      <Paper 
        elevation={6} 
        sx={{ 
          mt: 6, 
          p: 4, 
          borderRadius: 4,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}
      >
        <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom>
          ¿Por qué Elegirnos?
        </Typography>
        <Divider sx={{ mb: 4, bgcolor: 'white', height: 2 }} />
        
        <Grid container spacing={4}>
          {[
            {
              title: 'Respuesta Rápida',
              description: 'Respondemos a todas las consultas en menos de 24 horas.'
            },
            {
              title: 'Asesoría Especializada',
              description: 'Nuestro equipo de expertos te brindará la mejor orientación.'
            },
            {
              title: 'Soluciones Personalizadas',
              description: 'Desarrollamos propuestas adaptadas a tus necesidades específicas.'
            },
            {
              title: 'Compromiso Total',
              description: 'Nos comprometemos con el éxito de tu proyecto desde el primer contacto.'
            }
          ].map((item, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
              <Box textAlign="center" p={2}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {item.title}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                  {item.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Container>
  );
};

export default Contacto;
